import { observable, runInAction, action, computed } from 'mobx';
import { RouterStore, UsersStore } from '../../common/stores';
import ScriptManagementService from '../services/ScriptManagementService';
import message from 'antd/lib/message';
import {
    Script,
    SqlResultRow,
    CreateScriptRequest,
    UpdateScriptRequest,
    UsageInProjects,
    SqlQueryParameter,
    Environment
} from '../types';
import { Utils } from '../../common/services/Utils';

export interface SQLScriptRouterParams {
    scriptId: string;
}

type CreateSqlScriptRequest = CreateScriptRequest;
type UpdateSqlScriptRequest = UpdateScriptRequest;

export default class ScriptManagementStore {
    @observable
    scripts: Script[] = [];

    @observable
    isLoadingScripts: boolean = false;

    @observable
    isLoading: boolean = false;

    @observable
    isSaving: boolean = false;

    @observable
    isLoadingUsages: boolean = false;

    @observable
    isLoadingUsagesInProjects: boolean = false;

    @observable
    hasUsagesInProjectsError: boolean = false;

    @observable
    selectedScriptId: string | null = null;

    @observable
    results: SqlResultRow[] = [];

    @observable
    columns: string[] = [];

    @observable
    testRunError: string | null = null;

    @observable
    isDryRunModalVisible: boolean = false;

    @observable
    isRevisionModalVisible: boolean = false;

    @observable
    isRestoring: boolean = false;

    @observable
    environments: Environment[] = [];

    @observable
    isLoadingEnvironments: boolean = false;

    @observable
    selectedEnvironmentId: string | undefined = undefined;

    @observable
    customEndpointUrl: string = '';

    @observable
    useCustomEndpoint: boolean = false;

    @observable
    scriptUsagesMap = observable.map<string, UsageInProjects[]>();

    @observable
    scriptsUsages: UsageInProjects[] = [];

    private usagesInProjectsRequestId = 0;

    @computed
    get selectedScript() {
        return this.scripts.find(s => s.id === this.selectedScriptId);
    }

    @computed
    get sortedScripts() {
        return [...this.scripts].sort((a, b) => Utils.safeDateStringCompare(b.createDate, a.createDate));
    }

    @computed
    get scriptIds() {
        return this.scripts.map(s => s.id);
    }

    constructor(
        private routerStore: RouterStore,
        private userStore: UsersStore,
        private service: ScriptManagementService
    ) {}

    @action.bound
    async loadScripts() {
        try {
            runInAction(() => {
                this.isLoadingScripts = true;
            });

            const data = await this.service.getScripts();

            runInAction(() => {
                this.scripts = data;
            });
        } catch (err) {
            console.error(err);
            message.error('Failed to load scripts');
        } finally {
            runInAction(() => {
                this.isLoadingScripts = false;
            });
        }
    }

    @action.bound
    async loadScript(scriptId: string): Promise<Script | null> {
        try {
            runInAction(() => {
                this.isLoading = true;
            });

            const script = await this.service.getScript(scriptId);

            runInAction(() => {
                const existingIndex = this.scripts.findIndex(s => s.id === scriptId);
                if (existingIndex !== -1) {
                    this.scripts[existingIndex] = script;
                }
            });

            return script;
        } catch (err) {
            console.error(err);
            message.error('Failed to load script');
            return null;
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @action.bound
    async createScript(data: CreateSqlScriptRequest): Promise<boolean> {
        try {
            runInAction(() => {
                this.isSaving = true;
            });

            const resp = await this.service.createScript(data);

            if (!resp.isOk()) {
                message.error(resp.error?.data?.title || 'Failed to create script');
                return false;
            }

            runInAction(() => this.scripts.push(resp.value));
            message.success('Script created successfully');

            return true;
        } catch (err) {
            console.error(err);
            message.error('Failed to create script');
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async updateScript(scriptId: string, data: UpdateSqlScriptRequest): Promise<boolean> {
        try {
            runInAction(() => {
                this.isSaving = true;
            });

            const resp = await this.service.updateScript(scriptId, data);

            if (!resp.isOk()) {
                message.error(resp.error?.data?.title || 'Failed to update script');
                return false;
            }

            message.success('Script updated successfully');

            return true;
        } catch (err) {
            console.error(err);
            message.error('Failed to update script');
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action.bound
    async checkScriptUsage(scriptId: string) {
        try {
            runInAction(() => {
                this.isLoadingUsages = true;
            });

            return await this.service.checkScriptUsage(scriptId);
        } catch (err) {
            console.error(err);
            message.error('Failed to check script usage');
            return [];
        } finally {
            runInAction(() => {
                this.isLoadingUsages = false;
            });
        }
    }

    @action.bound
    async loadScriptUsagesInProjects() {
        if (this.scriptIds.length === 0) {
            return;
        }

        const requestId = ++this.usagesInProjectsRequestId;

        try {
            runInAction(() => {
                this.isLoadingUsagesInProjects = true;
                this.hasUsagesInProjectsError = false;
            });

            const usages = await this.service.checkScriptUsageInProjects(this.scriptIds);

            if (requestId !== this.usagesInProjectsRequestId) {
                return;
            }

            runInAction(() => {
                this.scriptsUsages = usages;
            });
        } catch (err) {
            if (requestId !== this.usagesInProjectsRequestId) {
                return;
            }

            console.error(err);
            message.error('Failed to load script usages');
            runInAction(() => {
                this.hasUsagesInProjectsError = true;
            });
        } finally {
            if (requestId === this.usagesInProjectsRequestId) {
                runInAction(() => {
                    this.isLoadingUsagesInProjects = false;
                });
            }
        }
    }

    @action.bound
    async restoreScriptRevision(scriptId: string, revisionId: string): Promise<Script | null> {
        try {
            runInAction(() => {
                this.isRestoring = true;
            });

            const resp = await this.service.restoreScriptRevision(scriptId, revisionId);

            if (!resp.isOk()) {
                message.error(resp.error?.data?.title || 'Failed to restore script revision');
                return null;
            }

            message.success('Script restored to selected revision successfully');

            return resp.value;
        } catch (err) {
            console.error(err);
            message.error('Failed to restore script revision');
            return null;
        } finally {
            runInAction(() => {
                this.isRestoring = false;
            });
        }
    }

    @action.bound
    async deleteScript(scriptId: string): Promise<boolean> {
        try {
            const resp = await this.service.deleteScript(scriptId);

            if (!resp.isOk()) {
                message.error(resp.error?.data?.title || 'Failed to delete script');
                return false;
            }

            runInAction(() => {
                this.scripts = this.scripts.filter(s => s.id !== scriptId);
            });

            message.success('Script deleted successfully');

            return true;
        } catch (err) {
            console.error(err);
            message.error('Failed to delete script');
            return false;
        }
    }

    @action.bound
    async executeDryRun(sqlBody: string, parameters: SqlQueryParameter[]) {
        try {
            if (!this.selectedEnvironmentId) {
                message.error('Please select an environment');
                return;
            }

            const endpointUrl = this.useCustomEndpoint && this.customEndpointUrl ? this.customEndpointUrl : undefined;
            const resp = await this.service.executeDryRun({
                environmentId: this.selectedEnvironmentId,
                sqlBody,
                parameters,
                endpointUrl
            });

            if (resp.isOk()) {
                this.results = resp.value.result || [];
                this.columns = resp.value.columns || [];
            } else {
                const errorMessage = resp.error?.data?.title || 'Failed to execute dry run';
                this.testRunError = errorMessage;
            }
        } catch (err) {
            console.error(err);
            message.error('Failed to execute dry run');
        }
    }

    @action.bound
    async fetchEnvironments() {
        try {
            runInAction(() => {
                this.isLoadingEnvironments = true;
            });

            const endpointUrl = this.useCustomEndpoint && this.customEndpointUrl ? this.customEndpointUrl : undefined;

            const resp = await this.service.getEnvironments(endpointUrl);

            if (!resp.isOk()) {
                runInAction(() => {
                    this.environments = [];
                    this.selectedEnvironmentId = undefined;
                });
                message.error(resp.error?.data?.title || 'Failed to fetch environments');
                return;
            }

            runInAction(() => {
                this.environments = resp.value.environments || [];
                if (this.environments.length > 0) {
                    this.selectedEnvironmentId = this.environments[0].id;
                }
            });
        } catch (err) {
            console.error('Failed to fetch environments:', err);
            message.error('Failed to fetch environments');
        } finally {
            runInAction(() => {
                this.isLoadingEnvironments = false;
            });
        }
    }

    @action.bound
    setSelectedEnvironmentId(id: string | undefined) {
        this.selectedEnvironmentId = id;
    }

    @action.bound
    setUseCustomEndpoint(value: boolean) {
        this.useCustomEndpoint = value;
        if (!value) {
            this.customEndpointUrl = '';
            this.fetchEnvironments();
        }
    }

    @action.bound
    setCustomEndpointUrl(url: string) {
        this.customEndpointUrl = url;
    }

    @action.bound
    setSelectedScript(params: SQLScriptRouterParams) {
        this.selectedScriptId = params.scriptId || null;
    }

    @action.bound
    openDryRunModal() {
        this.isDryRunModalVisible = true;
    }

    @action.bound
    closeDryRunModal() {
        this.isDryRunModalVisible = false;
        this.results = [];
        this.columns = [];
        this.testRunError = null;
        this.environments = [];
        this.selectedEnvironmentId = undefined;
        this.customEndpointUrl = '';
        this.useCustomEndpoint = false;
    }

    @action.bound
    openRevisionModal() {
        this.isRevisionModalVisible = true;
    }

    @action.bound
    closeRevisionModal() {
        this.isRevisionModalVisible = false;
    }

    @action.bound
    setIsRestoring(value: boolean) {
        this.isRestoring = value;
    }

    @action.bound
    resetDryRunResults() {
        this.results = [];
        this.columns = [];
        this.testRunError = null;
    }

    @computed
    get isLoadingUsers() {
        return this.userStore.isLoading;
    }

    @action.bound
    getUserNameById(id: string) {
        return this.userStore.getUserNameById(id);
    }

    getScriptUsages(scriptId: string): UsageInProjects[] {
        return this.scriptsUsages.filter(usage => usage.scriptId === scriptId);
    }

    navigateToCreateScript() {
        this.routerStore.push(`/admin/script-management/create`);
    }

    navigateToEditScript(scriptId: string) {
        this.routerStore.push(`/admin/script-management/${scriptId}/edit`);
    }

    navigateToList() {
        this.routerStore.push(`/admin/script-management`);
    }
}
